import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfirmationModal, FullColumn, normalizeDescription } from 'seagull';
import HelpMeWriteDrawer from '~/components/ai/HelpMeWriteDrawer/HelpMeWriteDrawer';
import ServiceDescription from '~/components/ServiceBuilder/ServiceDescription/ServiceDescription';
import ServiceNameTextField from '~/components/ServiceBuilder/ServiceNameTextField/ServiceNameTextField';
import ServiceTerms from '~/components/ServiceBuilder/ServiceTerms/ServiceTerms';
import { useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { EditServiceContentModalTestIds } from './EditServiceContentModal.utils';
const EditServiceContentModal = (param)=>{
    let { serviceName, description, terms, serviceId, title, width, dismissModal, onSave, serviceNameBlacklist, bundle, showAiDescriptionPanel: showAiDescriptionPanelProp = false, disableVariablePicker } = param;
    const { logBI } = useStoreActions();
    const { t: commonTexts } = useLocaleKeys('common');
    const { SERVICE_LEVEL_TERMS } = useFeatureFlags();
    const form = useForm({
        defaultValues: useMemo(()=>({
                serviceName,
                description,
                terms,
                bundle: bundle === null || bundle === void 0 ? void 0 : bundle.services
            }), [
            serviceName,
            description,
            terms,
            bundle === null || bundle === void 0 ? void 0 : bundle.services
        ])
    });
    const [isHelpMeWriteDrawerOpen, setIsHelpMeWriteDrawerOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(564);
    const calculateDrawerWidth = useCallback(()=>{
        if (!width) {
            setDrawerWidth(564);
            return;
        }
        const modalElement = document.querySelector('[data-testid="'.concat(EditServiceContentModalTestIds.EditServiceModal(), '"]'));
        if (!modalElement) {
            setDrawerWidth(564);
            return;
        }
        const modalRect = modalElement.getBoundingClientRect();
        const translationAmount = 250;
        const modalRightEdgeAfterTranslation = modalRect.right - translationAmount;
        const gap = 32;
        const rightMargin = 32;
        const calculatedWidth = window.innerWidth - modalRightEdgeAfterTranslation - gap - rightMargin;
        setDrawerWidth(Math.min(600, Math.max(300, calculatedWidth)));
    }, [
        width
    ]);
    useEffect(()=>{
        const timeoutId = setTimeout(()=>{
            calculateDrawerWidth();
        }, 0);
        const handleResize = ()=>{
            calculateDrawerWidth();
        };
        window.addEventListener('resize', handleResize);
        return ()=>{
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
        };
    }, [
        width,
        calculateDrawerWidth
    ]);
    const onSubmit = (data)=>{
        onSave({
            name: data.serviceName,
            description: data.description,
            terms: data.terms
        });
    };
    const isBundle = Boolean(bundle === null || bundle === void 0 ? void 0 : bundle.services);
    const showAiDescriptionPanel = !isBundle && showAiDescriptionPanelProp;
    const disableAiDescriptionPanel = showAiDescriptionPanel && form.watch('serviceName') === '';
    const onHelpMeWriteClick = showAiDescriptionPanel ? ()=>{
        logBI(17001);
        if (!isHelpMeWriteDrawerOpen) {
            calculateDrawerWidth();
        }
        setIsHelpMeWriteDrawerOpen((prev)=>!prev);
    } : undefined;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ConfirmationModal, {
                bi: {
                    primaryButton: {
                        name: 'edit_service_content_confirm'
                    },
                    secondaryButton: {
                        name: 'edit_service_content_cancel'
                    }
                },
                width: width,
                testId: EditServiceContentModalTestIds.EditServiceModal(),
                title: title,
                style: {
                    transform: isHelpMeWriteDrawerOpen ? 'translateX(-250px)' : 'translateX(0)',
                    transition: 'transform 0.2s ease-in-out'
                },
                content: /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
                    children: /*#__PURE__*/ _jsx("form", {
                        onSubmit: form.handleSubmit(onSubmit),
                        children: /*#__PURE__*/ _jsxs(FullColumn, {
                            gap: 24,
                            children: [
                                /*#__PURE__*/ _jsx(ServiceNameTextField, {
                                    testId: EditServiceContentModalTestIds.EditServiceNameInput(),
                                    serviceNameBlacklist: serviceNameBlacklist,
                                    disabled: isHelpMeWriteDrawerOpen,
                                    disableVariablePicker: disableVariablePicker
                                }),
                                /*#__PURE__*/ _jsx(ServiceDescription, {
                                    onHelpMeWriteClick: onHelpMeWriteClick,
                                    disabledHelpMeWrite: disableAiDescriptionPanel,
                                    isDrawerOpen: isHelpMeWriteDrawerOpen,
                                    testId: EditServiceContentModalTestIds.EditServiceDescriptionInput(),
                                    disabled: isHelpMeWriteDrawerOpen,
                                    disableVariablePicker: disableVariablePicker
                                }),
                                SERVICE_LEVEL_TERMS && !isBundle && /*#__PURE__*/ _jsx(ServiceTerms, {
                                    testId: EditServiceContentModalTestIds.EditServiceTermsInput(),
                                    disabled: isHelpMeWriteDrawerOpen,
                                    disableVariablePicker: disableVariablePicker
                                })
                            ]
                        })
                    })
                })),
                onPrimaryButtonClick: form.handleSubmit(onSubmit),
                onSecondaryButtonClick: ()=>dismissModal(),
                primaryButtonLabel: commonTexts.buttons.save(),
                secondaryButtonLabel: commonTexts.buttons.cancel(),
                primaryButtonTestId: EditServiceContentModalTestIds.EditServiceModalSaveButton()
            }),
            showAiDescriptionPanel && /*#__PURE__*/ _jsx(HelpMeWriteDrawer, {
                open: isHelpMeWriteDrawerOpen,
                onClose: ()=>setIsHelpMeWriteDrawerOpen(false),
                serviceName: serviceName,
                serviceDescription: description,
                serviceId: serviceId,
                drawerWidth: drawerWidth,
                onInsert: (parsedRichText)=>{
                    form.setValue('description', normalizeDescription(parsedRichText), {
                        shouldDirty: true
                    });
                }
            })
        ]
    });
};
export default EditServiceContentModal;
